import React from 'react';
import type { TransferRecord, TransferRecordLine } from '@/data/transferRecord';
import { formatCount } from '@/domain/warehouse';
import GroupedDataTable, { type GroupedTableColumn } from '../../../components/common/GroupedDataTable';
import { StatusChip } from '../../../components/status/StatusChip';
import TransferActionButton from './TransferActionButton';
import {
  filterLines,
  groupLinesByCategory,
  isR2Record,
  lineDisplayName,
  lineFilterLabels,
  lineIsDone,
  linePhysicalGuard,
  lineSku,
  lineStage,
  lineTrackingNote,
  unitsLeftToPick,
  unitsLeftToReceive,
  type LineFilter,
  type LineStage,
  type TransferActionViews,
} from './transferRecordView';

/** Long transfers (the largest has 553 lines) render in steps. */
const LINE_STEP = 100;

const EDITABLE_STATUSES = new Set(['draft', 'confirmed', 'partially_picked', 'picked']);
const RECEIVING_STATUSES = new Set(['partially_shipped', 'shipped', 'in_transit', 'partially_received']);

type TransferLinesSectionProps = {
  record: TransferRecord;
  actions: TransferActionViews;
  isPhone: boolean;
  busy: boolean;
  onAddLine: () => void;
  onEditLine: (lineId: string) => void;
  /** Opens Receive for these lines. */
  onReceiveLines: (lineIds: string[]) => void;
};

type LineEditState = { visible: boolean; allowed: boolean; reason: string | null };

function lineEditState(line: TransferRecordLine, record: TransferRecord, actions: TransferActionViews): LineEditState {
  if (isR2Record(record) || !EDITABLE_STATUSES.has(record.transfer.status) || line.status === 'cancelled') {
    return { visible: false, allowed: false, reason: null };
  }
  const guard = linePhysicalGuard(line);
  if (guard.locked) return { visible: true, allowed: false, reason: guard.message };
  return { visible: true, allowed: actions.edit_lines.allowed, reason: actions.edit_lines.reason };
}

function LineName({ line }: { line: TransferRecordLine }) {
  const notes = [
    line.sourceEvent?.code ? `For ${line.sourceEvent.code}` : null,
    line.shortQty > 0 ? `${formatCount(line.shortQty)} shipped short` : null,
    line.notes,
  ].filter(Boolean);
  return (
    <>
      <span className="transfer-record-line__name">{lineDisplayName(line)}</span>
      {notes.length ? <span className="grouped-data-table__sub">{notes.join(' · ')}</span> : null}
    </>
  );
}

function LineSku({ line }: { line: TransferRecordLine }) {
  const sku = lineSku(line);
  const note = lineTrackingNote(line);
  return (
    <>
      {sku ? <span className="transfer-record-line__sku">{sku}</span> : null}
      <span className={`grouped-data-table__sub${line.serials.length ? ' transfer-record-line__serials' : ''}`} title={note}>{note}</span>
    </>
  );
}

/** Lines: one list, grouped by category, with the receive selection and the totals strip. */
export default function TransferLinesSection({
  record,
  actions,
  isPhone,
  busy,
  onAddLine,
  onEditLine,
  onReceiveLines,
}: TransferLinesSectionProps) {
  const [filter, setFilter] = React.useState<LineFilter>('all');
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(() => new Set());
  const [cardLimit, setCardLimit] = React.useState(LINE_STEP);
  const r2 = isR2Record(record);
  const stage: LineStage = lineStage(record);
  const labels = lineFilterLabels(stage);
  const status = record.transfer.status;
  const receivingOpen = !r2 && RECEIVING_STATUSES.has(status);
  const selectable = receivingOpen && actions.receive.allowed;
  const showAddItem = !r2 && EDITABLE_STATUSES.has(status);
  const showLineEdits = showAddItem;

  const counts = React.useMemo(() => {
    const done = record.lines.filter((line) => lineIsDone(line, stage)).length;
    return { all: record.lines.length, open: record.lines.length - done, done };
  }, [record.lines, stage]);

  const grouped = React.useMemo(
    () => groupLinesByCategory(filterLines(record.lines, filter, stage)),
    [filter, record.lines, stage],
  );

  const receivableIds = React.useMemo(
    () => grouped.lines.filter((line) => line.status !== 'cancelled' && line.shippedQty > line.receivedQty).map((line) => line.id),
    [grouped.lines],
  );

  // Selections follow the record: lines received since (or filtered away) drop out.
  React.useEffect(() => {
    setSelected((current) => {
      if (!current.size) return current;
      const open = new Set(record.lines.filter((line) => line.shippedQty > line.receivedQty).map((line) => line.id));
      const next = new Set(Array.from(current).filter((id) => open.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [record.lines]);

  React.useEffect(() => {
    setCardLimit(LINE_STEP);
  }, [filter]);

  const toggleLine = React.useCallback((lineId: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }, []);

  const allSelected = receivableIds.length > 0 && receivableIds.every((id) => selected.has(id));
  const someSelected = receivableIds.some((id) => selected.has(id));

  const toggleAll = React.useCallback(() => {
    setSelected((current) => {
      const next = new Set(current);
      if (receivableIds.every((id) => next.has(id))) receivableIds.forEach((id) => next.delete(id));
      else receivableIds.forEach((id) => next.add(id));
      return next;
    });
  }, [receivableIds]);

  const handleReceiveSelected = React.useCallback(() => {
    onReceiveLines(Array.from(selected));
  }, [onReceiveLines, selected]);

  const selectAllRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [allSelected, someSelected]);

  const columns = React.useMemo<GroupedTableColumn<TransferRecordLine>[]>(() => {
    const list: GroupedTableColumn<TransferRecordLine>[] = [];
    if (selectable) {
      list.push({
        key: 'select',
        width: '2.6rem',
        label: 'Select',
        header: (
          <input
            ref={selectAllRef}
            type="checkbox"
            className="transfer-record-line__check"
            aria-label="Select all lines to receive"
            checked={allSelected}
            disabled={!receivableIds.length}
            onChange={toggleAll}
          />
        ),
        render: (line) => (line.status !== 'cancelled' && line.shippedQty > line.receivedQty ? (
          <input
            type="checkbox"
            className="transfer-record-line__check"
            aria-label={`Select ${lineDisplayName(line)}`}
            checked={selected.has(line.id)}
            onChange={() => toggleLine(line.id)}
          />
        ) : null),
      });
    }
    list.push(
      { key: 'item', header: 'Item', render: (line) => <LineName line={line} /> },
      { key: 'sku', header: 'SKU / serials', width: '24%', render: (line) => <LineSku line={line} /> },
      { key: 'qty', header: 'Qty', width: '4.6rem', align: 'end', className: 'num', render: (line) => formatCount(line.qty) },
      { key: 'picked', header: 'Picked', width: '5rem', align: 'end', className: 'num', optional: true, render: (line) => formatCount(line.pickedQty) },
      { key: 'shipped', header: 'Shipped', width: '5.2rem', align: 'end', className: 'num', optional: true, render: (line) => formatCount(line.shippedQty) },
      { key: 'received', header: 'Received', width: '5.6rem', align: 'end', className: 'num', render: (line) => formatCount(line.receivedQty) },
      { key: 'status', header: 'Status', width: '9.5rem', render: (line) => <StatusChip kind="transferLine" status={line.status} size="sm" /> },
    );
    if (showLineEdits) {
      list.push({
        key: 'edit',
        header: <span className="sr-only">Line actions</span>,
        label: 'Actions',
        width: '5rem',
        align: 'end',
        render: (line) => {
          const state = lineEditState(line, record, actions);
          if (!state.visible) return null;
          return (
            <TransferActionButton
              className="btn xs transfer-record-line__edit"
              allowed={state.allowed}
              reason={state.reason}
              busy={busy}
              ariaLabel={`Edit ${lineDisplayName(line)}`}
              onClick={() => onEditLine(line.id)}
            >
              Edit
            </TransferActionButton>
          );
        },
      });
    }
    return list;
  }, [actions, allSelected, busy, onEditLine, receivableIds.length, record, selectable, selected, showLineEdits, toggleAll, toggleLine]);

  const getRowKey = React.useCallback((line: TransferRecordLine) => line.id, []);
  const getGroupKey = React.useCallback((line: TransferRecordLine) => line.category?.trim() || 'Other', []);

  const left = stage === 'receive' ? unitsLeftToReceive(record) : unitsLeftToPick(record);
  const filterOptions: Array<{ key: LineFilter; label: string; count: number }> = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'open', label: labels.open, count: counts.open },
    { key: 'done', label: labels.done, count: counts.done },
  ];
  const emptyText = record.lines.length
    ? 'No lines match this filter.'
    : r2 ? 'R2 lists no lines on this transfer.' : 'No lines yet. Add the items to move.';
  const visibleCards = grouped.lines.slice(0, cardLimit);

  return (
    <div className="transfer-record-panel">
      <div className="transfer-record-panel__head">
        <div className="transfer-record-panel__heading">
          <h2 className="transfer-record-panel__title">Transfer lines</h2>
          <p className="transfer-record-panel__intro">One list of lines. Quantities lock once picked; serials show on each line.</p>
        </div>
        <div className="transfer-record-panel__actions">
          {showAddItem ? (
            <TransferActionButton
              className="btn sm"
              allowed={actions.edit_lines.allowed}
              reason={actions.edit_lines.reason}
              busy={busy}
              onClick={onAddLine}
            >
              Add item
            </TransferActionButton>
          ) : null}
          {receivingOpen ? (
            <TransferActionButton
              className="btn sm"
              allowed={actions.receive.allowed && selected.size > 0}
              reason={actions.receive.allowed ? 'Tick the lines to receive first.' : actions.receive.reason}
              busy={busy}
              onClick={handleReceiveSelected}
            >
              {selected.size > 0 ? `Receive selected (${selected.size})` : 'Receive selected'}
            </TransferActionButton>
          ) : null}
        </div>
      </div>

      <div className="transfer-record-lines__bar">
        <div className="transfer-record-facet" role="group" aria-label="Show lines">
          {filterOptions.map((option) => (
            <button
              key={option.key}
              type="button"
              className="transfer-record-facet__option"
              aria-pressed={filter === option.key}
              onClick={() => setFilter(option.key)}
            >
              {option.label}
              <span className="transfer-record-facet__count">{formatCount(option.count)}</span>
            </button>
          ))}
        </div>
        <span className="transfer-record-lines__sort">Sorted by category</span>
      </div>

      {isPhone ? (
        <>
          {grouped.lines.length ? (
            <ul className="transfer-record-line-cards" aria-label="Transfer lines">
              {visibleCards.map((line) => {
                const done = stage === 'receive' ? line.receivedQty : line.pickedQty;
                const of = stage === 'receive' ? (line.shippedQty || line.qty) : line.qty;
                const canSelect = selectable && line.status !== 'cancelled' && line.shippedQty > line.receivedQty;
                const edit = showLineEdits ? lineEditState(line, record, actions) : null;
                return (
                  <li key={line.id} className="transfer-record-line-card" data-done={lineIsDone(line, stage) || undefined}>
                    {canSelect ? (
                      <input
                        type="checkbox"
                        className="transfer-record-line__check"
                        aria-label={`Select ${lineDisplayName(line)}`}
                        checked={selected.has(line.id)}
                        onChange={() => toggleLine(line.id)}
                      />
                    ) : null}
                    <div className="transfer-record-line-card__main">
                      <strong>{lineDisplayName(line)}</strong>
                      <small>{lineTrackingNote(line)}</small>
                    </div>
                    <span className="transfer-record-line-card__qty" aria-label={`${formatCount(done)} of ${formatCount(of)} ${stage === 'receive' ? 'received' : 'picked'}`}>
                      {formatCount(done)} / {formatCount(of)}
                      <small>{stage === 'receive' ? 'in' : 'picked'}</small>
                    </span>
                    {edit?.visible ? (
                      <TransferActionButton
                        className="btn xs transfer-record-line__edit"
                        allowed={edit.allowed}
                        reason={edit.reason}
                        busy={busy}
                        ariaLabel={`Edit ${lineDisplayName(line)}`}
                        onClick={() => onEditLine(line.id)}
                      >
                        Edit
                      </TransferActionButton>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="transfer-record-empty">{emptyText}</p>
          )}
          {grouped.lines.length > visibleCards.length ? (
            <div className="transfer-record-more">
              <button type="button" className="btn xs" onClick={() => setCardLimit((limit) => limit + LINE_STEP)}>
                Show {Math.min(LINE_STEP, grouped.lines.length - visibleCards.length)} more
              </button>
              <span>{formatCount(grouped.lines.length - visibleCards.length)} not shown yet</span>
            </div>
          ) : null}
        </>
      ) : (
        <GroupedDataTable
          ariaLabel="Transfer lines"
          className="transfer-record-lines"
          columns={columns}
          rows={grouped.lines}
          getRowKey={getRowKey}
          getGroupKey={getGroupKey}
          groups={grouped.groups}
          emptyState={emptyText}
          initialRowLimit={LINE_STEP}
        />
      )}

      <dl className="transfer-record-totals" aria-label="Line totals">
        <div><dt>Lines</dt><dd>{formatCount(record.totals.lineCount)}</dd></div>
        <div><dt>Units</dt><dd>{formatCount(record.totals.units)}</dd></div>
        <div><dt>Picked</dt><dd>{formatCount(record.totals.picked)}</dd></div>
        <div><dt>Shipped</dt><dd>{formatCount(record.totals.shipped)}</dd></div>
        <div><dt>Received</dt><dd>{formatCount(record.totals.received)}</dd></div>
        <div className="transfer-record-totals__grand">
          <dt>{stage === 'receive' ? 'Still to receive' : 'Still to pick'}</dt>
          <dd>{formatCount(left)}</dd>
        </div>
      </dl>
    </div>
  );
}
